'use client';

import classNames from 'classnames';
import React from 'react';
import { usePagination, UsePaginationProps } from 'react-instantsearch';

const scrollToTop = function () {
    setTimeout(() => {
        document.querySelector('div[data-insights-index]')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 250);
};

const isModifierClick = function (event: React.MouseEvent) {
    const isMiddleClick = event.button === 1;

    return Boolean(isMiddleClick || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey);
};

function CustomPagination(props: UsePaginationProps) {
    const { pages, currentRefinement, nbPages, isFirstPage, isLastPage, refine, createURL } = usePagination(props);
    const firstPageIndex = 0;
    const previousPageIndex = currentRefinement - 1;
    const nextPageIndex = currentRefinement + 1;
    const lastPageIndex = nbPages - 1;
    const paginationClasses = classNames('ais-Pagination', { 'ais-Pagination--noRefinement': nbPages <= 1 });

    return (
        <div className={paginationClasses}>
            <ul className="ais-Pagination-list">
                <PaginationItem
                    isDisabled={isFirstPage}
                    href={createURL(firstPageIndex)}
                    onClick={() => refine(firstPageIndex)}
                    additionalClass="ais-Pagination-item--firstPage"
                    aria-label="First Page"
                >
                    ‹‹
                </PaginationItem>
                <PaginationItem
                    isDisabled={isFirstPage}
                    href={createURL(previousPageIndex)}
                    onClick={() => refine(previousPageIndex)}
                    additionalClass="ais-Pagination-item--previousPage"
                    aria-label="Previous Page"
                >
                    ‹
                </PaginationItem>
                {pages.map((page) => {
                    const label = page + 1;

                    return (
                        <PaginationItem
                            key={page}
                            isDisabled={false}
                            isCurrent={currentRefinement === page}
                            aria-label={`Page ${label}`}
                            href={createURL(page)}
                            onClick={() => refine(page)}
                            additionalClass="ais-Pagination-item--page"
                        >
                            {label}
                        </PaginationItem>
                    );
                })}
                <PaginationItem
                    isDisabled={isLastPage}
                    href={createURL(nextPageIndex)}
                    onClick={() => refine(nextPageIndex)}
                    additionalClass="ais-Pagination-item--nextPage"
                    aria-label="Next Page"
                >
                    ›
                </PaginationItem>
                <PaginationItem
                    isDisabled={isLastPage}
                    href={createURL(lastPageIndex)}
                    onClick={() => refine(lastPageIndex)}
                    additionalClass="ais-Pagination-item--lastPage"
                    aria-label={'Last Page, Page ' + nbPages}
                >
                    ››
                </PaginationItem>
            </ul>
        </div>
    );
}

type PaginationItemProps = Omit<React.ComponentProps<'a'>, 'onClick'> & {
    onClick: NonNullable<React.ComponentProps<'a'>['onClick']>;
    isDisabled: boolean;
    isCurrent?: boolean;
    additionalClass?: string;
};

function PaginationItem({ isDisabled, isCurrent, href, onClick, additionalClass, ...props }: PaginationItemProps) {
    const itemClasses = classNames('ais-Pagination-item', additionalClass, {
        'ais-Pagination-item--selected': isCurrent,
        'ais-Pagination-item--disabled': isDisabled,
    });

    if (isDisabled) {
        return (
            <li className={itemClasses}>
                <span className="ais-Pagination-link" {...props} />
            </li>
        );
    }

    return (
        <li className={itemClasses}>
            <a
                href={href}
                className="ais-Pagination-link"
                onClick={(event) => {
                    if (isModifierClick(event)) return;

                    event.preventDefault();

                    onClick(event);
                    scrollToTop();
                }}
            >
                {props['children']}
                <span className="sr-only">{props['aria-label']}</span>
            </a>
        </li>
    );
}

export type SearchPaginationProps = Record<string, never>;

export function SearchPagination(props: SearchPaginationProps) {
    return <CustomPagination />;
}

export default SearchPagination;
